import type { TimeOffType, DayPart, RequestStatus } from "./shared/timeOffTypes";

export interface Me {
  email: string;
  name: string;
  isEligibleRequester: boolean;
  isApprover: boolean;
  isExecutive: boolean;
  inRise: boolean;
  directReports: { email: string; name: string }[];
  managerName: string | null;
}

export async function fetchMe(): Promise<Me | "unauthenticated"> {
  const res = await fetch("/api/me", { credentials: "include" });
  if (res.status === 401) return "unauthenticated";
  if (!res.ok) throw new Error(`Failed to load current user (${res.status})`);
  return (await res.json()) as Me;
}

export interface TimeOffRequest {
  id: string;
  person_email: string;
  person_name: string | null;
  type: TimeOffType;
  start_date: string;
  end_date: string;
  day_part: DayPart;
  note: string | null;
  status: RequestStatus;
  auto_approved: boolean;
  submitted_at: string;
  decided_by_email: string | null;
  decided_at: string | null;
  deny_reason: string | null;
  requester_timezone: string | null;
  /** Only populated on the Org-wide ("org" scope) request list. */
  decided_by_name?: string | null;
  /** Only populated on the Org-wide ("org" scope) request list -- the requester's manager, used as the "Acknowledged by" fallback for auto-approved Sick Time. */
  requester_manager_name?: string | null;
}

async function handleJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore -- fall back to the generic message above
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function fetchRequests(scope: "mine" | "team" | "org" | "calendar" | "backup", year?: number): Promise<TimeOffRequest[]> {
  const yearParam = year !== undefined ? `&year=${year}` : "";
  return fetch(`/api/requests?scope=${scope}${yearParam}`, { credentials: "include" }).then((res) =>
    handleJson<TimeOffRequest[]>(res)
  );
}

/** Years that have org-wide request data, for the year picker on the Org-wide page. Always includes the current year. */
export function fetchOrgYears(): Promise<number[]> {
  return fetch(`/api/requests?scope=org-years`, { credentials: "include" }).then((res) => handleJson<number[]>(res));
}

export function createRequest(payload: {
  type: TimeOffType;
  startDate: string;
  endDate: string;
  dayPart: DayPart;
  note?: string;
  timeZone?: string;
}): Promise<TimeOffRequest> {
  return fetch("/api/requests", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then((res) => handleJson<TimeOffRequest>(res));
}

export function cancelRequest(id: string): Promise<TimeOffRequest> {
  return fetch(`/api/requests/${id}/cancel`, {
    method: "POST",
    credentials: "include",
  }).then((res) => handleJson<TimeOffRequest>(res));
}

export function decideRequest(
  id: string,
  action: "approve" | "deny",
  reason?: string
): Promise<TimeOffRequest> {
  return fetch(`/api/requests/${id}/decide`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, reason }),
  }).then((res) => handleJson<TimeOffRequest>(res));
}

export interface ExecutiveListing {
  email: string;
  name: string;
  isExecutive: boolean;
}

export function fetchExecutiveAccessList(): Promise<ExecutiveListing[]> {
  return fetch("/api/admin/executives", { credentials: "include" }).then((res) =>
    handleJson<ExecutiveListing[]>(res)
  );
}

export function setExecutiveAccess(email: string, isExecutive: boolean): Promise<ExecutiveListing> {
  return fetch("/api/admin/executives", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, isExecutive }),
  }).then((res) => handleJson<ExecutiveListing>(res));
}

export interface ManagerAwayListing {
  email: string;
  name: string;
  manualAwayUntil: string | null;
}

export function fetchManagerAwayList(): Promise<ManagerAwayListing[]> {
  return fetch("/api/admin/manual-away", { credentials: "include" }).then((res) =>
    handleJson<ManagerAwayListing[]>(res)
  );
}

/** Pass `until: null` to clear an existing override. */
export function setManagerAway(email: string, until: string | null): Promise<ManagerAwayListing> {
  return fetch("/api/admin/manual-away", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, until }),
  }).then((res) => handleJson<ManagerAwayListing>(res));
}
